import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiCalendar, FiPlus, FiSearch, FiEdit2, FiTrash2, FiRefreshCw,
  FiMapPin, FiUsers, FiLink, FiDownload, FiUpload, FiClock,
  FiChevronDown, FiChevronUp, FiX, FiExternalLink, FiAward,
  FiCopy, FiRss, FiImage, FiGlobe
} from 'react-icons/fi';
import { eventsAPI, challengesAPI } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import LanguageTabsEditor from '../components/LanguageTabsEditor';
import { useTranslation } from '../hooks/useTranslation';

// ─── Constants ───────────────────────────────────────────────

const CATEGORIES = [
  { value: 'festival', label: 'Festival' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'competition', label: 'Competition' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'tour', label: 'Tour' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'sports', label: 'Sports' }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'cancelled', label: 'Cancelled' }
];

const getCategoryColor = (cat) => {
  const colors = {
    festival: 'bg-purple-100 text-purple-800',
    workshop: 'bg-blue-100 text-blue-800',
    competition: 'bg-red-100 text-red-800',
    meetup: 'bg-green-100 text-green-800',
    tour: 'bg-amber-100 text-amber-800',
    seasonal: 'bg-cyan-100 text-cyan-800',
    cultural: 'bg-indigo-100 text-indigo-800',
    sports: 'bg-orange-100 text-orange-800'
  };
  return colors[cat] || 'bg-gray-100 text-gray-800';
};

const getStatusColor = (status) => {
  const colors = {
    draft: 'bg-gray-100 text-gray-800',
    published: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const toInputDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toISOString().slice(0, 16);
};

// ─── Collapsible Section Component ───────────────────────────
// Defined outside of EventModal to prevent re-creation on each render

const CollapsibleSection = ({ id, title, bg, children, expandedSections, toggleSection }) => (
  <div className={`border border-gray-200 rounded-lg overflow-hidden ${bg || ''}`}>
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-50"
    >
      {title}
      {expandedSections[id] ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
    </button>
    {expandedSections[id] && <div className="px-4 pb-4 space-y-3">{children}</div>}
  </div>
);

// ─── Main Component ──────────────────────────────────────────

const Events = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', category: '', search: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Load data
  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;

      const [eventsRes, statsRes] = await Promise.all([
        eventsAPI.getEvents(params),
        eventsAPI.getEventStats()
      ]);
      setEvents(eventsRes.data.events || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Actions
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await eventsAPI.deleteEvent(id);
      toast.success('Event deleted');
      loadEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleRestore = async (id) => {
    try {
      await eventsAPI.restoreEvent(id);
      toast.success('Event restored');
      loadEvents();
    } catch (error) {
      toast.error('Failed to restore event');
    }
  };

  const openCreate = () => {
    setEditingEvent(null);
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await eventsAPI.getEventById(id);
      setEditingEvent(res.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load event');
    }
  };

  const downloadIcal = (event) => {
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
    window.open(`${apiUrl}/api/events/${event.id}/ical`, '_blank');
  };

  const copyFeedUrl = () => {
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
    const feedUrl = `${apiUrl}/api/events/feed.ics`;
    navigator.clipboard.writeText(feedUrl);
    toast.success('Calendar feed URL copied');
  };

  // ─── Stats Bar ─────────────────────────────────────────────

  const StatsBar = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {[
        { label: 'Total Events', value: stats?.total || 0, color: 'text-primary-600' },
        { label: 'Active Now', value: stats?.activeNow || 0, color: 'text-green-600' },
        { label: 'Upcoming', value: stats?.upcoming || 0, color: 'text-blue-600' },
        { label: 'Registrations', value: stats?.totalRegistrations || 0, color: 'text-purple-600' }
      ].map(s => (
        <div key={s.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{s.label}</p>
          <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
        </div>
      ))}
    </div>
  );

  // ─── Event Card ────────────────────────────────────────────

  const EventCard = ({ event }) => {
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
    const headerImg = event.header_image_url
      ? (event.header_image_url.startsWith('http') ? event.header_image_url : `${apiUrl}${event.header_image_url}`)
      : null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
        {/* Header image */}
        <div className="h-40 bg-gradient-to-br from-primary-100 to-primary-50 relative">
          {headerImg ? (
            <img src={headerImg} alt={event.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiCalendar className="w-12 h-12 text-primary-300" />
            </div>
          )}
          {/* Status badge */}
          <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(event.status)}`}>
            {event.status}
          </span>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 truncate">{event.title}</h3>

          {/* Date */}
          <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
            <FiClock className="w-3.5 h-3.5" />
            <span>{formatDate(event.start_date)}</span>
            {event.end_date && <span>- {formatDate(event.end_date)}</span>}
          </div>

          {/* Location */}
          {event.location_name && (
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              <FiMapPin className="w-3.5 h-3.5" />
              <span className="truncate">{event.location_name}</span>
            </div>
          )}

          {/* Badges row */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {event.category && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(event.category)}`}>
                {event.category}
              </span>
            )}
            {event.challenge_id && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 flex items-center gap-1">
                <FiLink className="w-3 h-3" /> Challenge
              </span>
            )}
            {event.nft_template_id && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                <FiAward className="w-3 h-3" /> NFT
              </span>
            )}
          </div>

          {/* Registrations */}
          {event.registration_count > 0 && (
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-2">
              <FiUsers className="w-3.5 h-3.5" />
              <span>{event.registration_count} registered</span>
              {event.max_participants && <span>/ {event.max_participants}</span>}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button onClick={() => openEdit(event.id)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded transition-colors">
              <FiEdit2 className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={() => downloadIcal(event)}
              className="flex items-center justify-center px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50 rounded transition-colors"
              title="Download .ics">
              <FiDownload className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDelete(event.id, event.title)}
              className="flex items-center justify-center px-2 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Delete">
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600 flex items-center gap-2">
            <FiCalendar className="w-6 h-6" /> {t('cms.nav.events')}
          </h1>
          <p className="text-gray-600">{t('cms.events.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyFeedUrl}
            className="btn btn-outline flex items-center gap-2 text-sm"
            title="Copy public iCal feed URL">
            <FiRss className="w-4 h-4" /> Feed URL
          </button>
          <button onClick={() => setShowImportModal(true)}
            className="btn btn-outline flex items-center gap-2 text-sm">
            <FiUpload className="w-4 h-4" /> Import
          </button>
          <button onClick={openCreate}
            className="btn btn-primary flex items-center gap-2">
            <FiPlus className="w-4 h-4" /> {t('cms.events.addEvent')}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && <StatsBar />}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('cms.common.search')}
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <select
            value={filters.category}
            onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <div className="flex gap-1">
            {[{ value: '', label: 'All' }, ...STATUS_OPTIONS].map(s => (
              <button key={s.value}
                onClick={() => setFilters(f => ({ ...f, status: s.value }))}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filters.status === s.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Event Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FiCalendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No events yet</h3>
          <p className="text-gray-500 mt-1">Create your first event or import from a calendar file.</p>
          <button onClick={openCreate} className="btn btn-primary mt-4">
            <FiPlus className="w-4 h-4 mr-2 inline" /> {t('cms.events.addEvent')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => <EventCard key={event.id} event={event} />)}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <EventModal
          event={editingEvent}
          onClose={() => { setShowModal(false); setEditingEvent(null); }}
          onSaved={() => { setShowModal(false); setEditingEvent(null); loadEvents(); }}
          t={t}
        />
      )}
      {showImportModal && (
        <ImportCalendarModal
          onClose={() => setShowImportModal(false)}
          onImported={() => { setShowImportModal(false); loadEvents(); }}
          t={t}
        />
      )}
    </div>
  );
};

// ─── Event Create/Edit Modal ─────────────────────────────────

const EventModal = ({ event, onClose, onSaved, t }) => {
  const isEdit = !!event;
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', description: '', content: '',
    category: '', status: 'draft',
    startDate: '', endDate: '', allDay: false, timezone: 'Europe/Berlin',
    locationName: '', locationAddress: '', latitude: '', longitude: '', radius: 200,
    challengeId: '', nftTemplateId: '',
    registrationEnabled: false, maxParticipants: '', registrationStart: '', registrationEnd: '', isPublic: true,
    recurrenceRule: null, seriesName: '',
    metaTitle: '', metaDescription: '', tags: [],
    challengeMode: 'none' // none, create, link
  });
  const [saving, setSaving] = useState(false);
  const [headerFile, setHeaderFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    basic: true, description: false, datetime: true, location: false,
    challenge: false, nft: false, registration: false, header: false, seo: false, translations: false, tags: false
  });
  const [tagInput, setTagInput] = useState('');

  // Translation state
  const [translations, setTranslations] = useState({
    en: { title: '', description: '', content: '' },
    de: { title: '', description: '', content: '' },
    es: { title: '', description: '', content: '' }
  });
  const [availableLanguages, setAvailableLanguages] = useState([
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' }
  ]);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (event) {
      const tags = event.metadata?.tags || [];
      setForm({
        title: event.title || '',
        slug: event.slug || '',
        excerpt: event.excerpt || '',
        description: event.description || '',
        content: event.content || '',
        category: event.category || '',
        status: event.status || 'draft',
        startDate: toInputDate(event.startDate) || '',
        endDate: toInputDate(event.endDate) || '',
        allDay: event.allDay || false,
        timezone: event.timezone || 'Europe/Berlin',
        locationName: event.locationName || '',
        locationAddress: event.locationAddress || '',
        latitude: event.latitude || '',
        longitude: event.longitude || '',
        radius: event.radius || 200,
        challengeId: event.challengeId || '',
        nftTemplateId: event.nftTemplateId || '',
        registrationEnabled: event.registrationEnabled || false,
        maxParticipants: event.maxParticipants || '',
        registrationStart: toInputDate(event.registrationStart) || '',
        registrationEnd: toInputDate(event.registrationEnd) || '',
        isPublic: event.isPublic !== false,
        recurrenceRule: event.recurrenceRule || null,
        seriesName: event.seriesName || '',
        metaTitle: event.metaTitle || '',
        metaDescription: event.metaDescription || '',
        tags,
        challengeMode: event.challengeId ? 'link' : 'none'
      });
      if (event.headerImageUrl) {
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
        setHeaderPreview(event.headerImageUrl.startsWith('http') ? event.headerImageUrl : `${apiUrl}${event.headerImageUrl}`);
      }
    }
    // Load challenges for linking
    loadChallenges();
  }, [event]);

  const loadChallenges = async () => {
    try {
      const res = await challengesAPI.getChallenges({ limit: 200 });
      setChallenges(res.data.challenges || res.data || []);
    } catch (e) { /* ignore */ }
  };

  // Fetch available languages for translations
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await api.get('/cms/settings/languages');
        if (response.data?.languages?.length > 0) {
          setAvailableLanguages(response.data.languages);
          // Initialize translations structure for all languages
          const initTrans = {};
          response.data.languages.forEach(lang => {
            initTrans[lang.code] = { title: '', description: '', content: '' };
          });
          setTranslations(initTrans);
        }
      } catch (error) {
        console.log('Using default languages');
      }
    };
    fetchLanguages();
  }, []);

  // Note: English translation fields are synced via the onChange handler in LanguageTabsEditor
  // which calls handleChange for English fields. No useEffect sync needed - this prevents
  // circular state updates that cause input focus loss.

  // Load existing translations when editing, including English from form fields
  useEffect(() => {
    if (event) {
      setTranslations(prev => ({
        ...prev,
        ...event.translations,
        // Also populate English from the main event fields
        en: {
          title: event.title || event.translations?.en?.title || '',
          description: event.description || event.translations?.en?.description || '',
          content: event.content || event.translations?.en?.content || ''
        }
      }));
    }
  }, [event]);

  // Initialize mini map for location picking
  useEffect(() => {
    if (!expandedSections.location || !mapRef.current || mapInstanceRef.current) return;

    // Dynamically load Leaflet if needed
    if (typeof window.L === 'undefined') return;

    const lat = parseFloat(form.latitude) || 47.27;
    const lng = parseFloat(form.longitude) || 11.39;
    const map = window.L.map(mapRef.current).setView([lat, lng], 13);
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    const marker = window.L.marker([lat, lng], { draggable: true }).addTo(map);
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      setForm(f => ({ ...f, latitude: pos.lat.toFixed(5), longitude: pos.lng.toFixed(5) }));
    });

    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
      setForm(f => ({ ...f, latitude: e.latlng.lat.toFixed(5), longitude: e.latlng.lng.toFixed(5) }));
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [expandedSections.location]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    // Also sync translatable fields to English translations
    if (['title', 'description', 'content'].includes(field)) {
      setTranslations(prev => ({
        ...prev,
        en: { ...prev.en, [field]: value }
      }));
    }
  };

  const handleHeaderFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setHeaderFile(file);
      setHeaderPreview(URL.createObjectURL(file));
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm(f => ({ ...f, tags: [...f.tags, tag] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }));
  };

  const handleSave = async () => {
    if (!form.title || !form.startDate) {
      toast.error('Title and start date are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        radius: parseInt(form.radius) || 200,
        maxParticipants: form.maxParticipants ? parseInt(form.maxParticipants) : null,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
        registrationStart: form.registrationStart ? new Date(form.registrationStart).toISOString() : null,
        registrationEnd: form.registrationEnd ? new Date(form.registrationEnd).toISOString() : null,
        metadata: { tags: form.tags }
      };

      // Don't send challengeId if mode is 'none' or 'create'
      if (form.challengeMode !== 'link') {
        payload.challengeId = null;
      }

      delete payload.challengeMode;

      // Add translations (filter out empty ones)
      const filteredTranslations = {};
      for (const [locale, content] of Object.entries(translations)) {
        if (content.title || content.description || content.content) {
          filteredTranslations[locale] = content;
        }
      }
      if (Object.keys(filteredTranslations).length > 0) {
        payload.translations = filteredTranslations;
      }

      let savedEvent;
      if (isEdit) {
        const res = await eventsAPI.updateEvent(event.id, payload);
        savedEvent = res.data.event;
      } else {
        const res = await eventsAPI.createEvent(payload);
        savedEvent = res.data.event;
      }

      // Upload header image if selected
      if (headerFile && savedEvent?.id) {
        await eventsAPI.uploadHeaderImage(savedEvent.id, headerFile);
      }

      // Create challenge if mode is 'create'
      if (form.challengeMode === 'create' && savedEvent?.id && !event?.challengeId) {
        try {
          await eventsAPI.createChallenge(savedEvent.id);
          toast.success('Challenge auto-created');
        } catch (e) {
          toast.error('Event saved but challenge creation failed');
        }
      }

      toast.success(isEdit ? 'Event updated' : 'Event created');
      onSaved();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // Section shorthand - uses sectionProps for cleaner JSX
  const sectionProps = { expandedSections, toggleSection };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? t('cms.events.editEvent') : t('cms.events.addEvent')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Modal body */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* Basic Info */}
          <CollapsibleSection {...sectionProps} id="basic" title="Basic Info">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input type="text" value={form.title}
                onChange={e => handleChange('title', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Event title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={form.category} onChange={e => handleChange('category', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500">
                  <option value="">Select...</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => handleChange('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500">
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
              <textarea value={form.excerpt} onChange={e => handleChange('excerpt', e.target.value)}
                rows={2} maxLength={500}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                placeholder="Short description (max 500 chars)" />
            </div>
          </CollapsibleSection>

          {/* Description */}
          <CollapsibleSection {...sectionProps} id="description" title="Description">
            <textarea value={form.description} onChange={e => handleChange('description', e.target.value)}
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
              placeholder="Full event description..." />
          </CollapsibleSection>

          {/* Date & Time */}
          <CollapsibleSection {...sectionProps} id="datetime" title="Date & Time">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.events.startDate')} *</label>
                <input type="datetime-local" value={form.startDate}
                  onChange={e => handleChange('startDate', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.events.endDate')}</label>
                <input type="datetime-local" value={form.endDate}
                  onChange={e => handleChange('endDate', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.allDay}
                  onChange={e => handleChange('allDay', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                All day event
              </label>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Timezone:</label>
                <select value={form.timezone} onChange={e => handleChange('timezone', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="Europe/Vienna">Europe/Vienna</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
          </CollapsibleSection>

          {/* Location */}
          <CollapsibleSection {...sectionProps} id="location" title={t('cms.events.location')}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                <input type="text" value={form.locationName}
                  onChange={e => handleChange('locationName', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. Rathaus Wien" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={form.locationAddress}
                  onChange={e => handleChange('locationAddress', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                  placeholder="Street, City" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                <input type="number" step="0.00001" value={form.latitude}
                  onChange={e => handleChange('latitude', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                <input type="number" step="0.00001" value={form.longitude}
                  onChange={e => handleChange('longitude', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Radius (m)</label>
                <input type="number" value={form.radius}
                  onChange={e => handleChange('radius', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div ref={mapRef} className="h-48 rounded-lg border border-gray-200 bg-gray-100"
              style={{ minHeight: '192px' }} />
            <p className="text-xs text-gray-500">Click on the map or drag the marker to set coordinates.</p>
          </CollapsibleSection>

          {/* Challenge Link */}
          <CollapsibleSection {...sectionProps} id="challenge" title={t('cms.events.linkedChallenge')} bg="bg-blue-50/50">
            <div className="flex gap-2">
              {['none', 'create', 'link'].map(mode => (
                <button key={mode} type="button"
                  onClick={() => handleChange('challengeMode', mode)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    form.challengeMode === mode
                      ? 'bg-blue-500 text-white'
                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}>
                  {mode === 'none' ? 'No Challenge' : mode === 'create' ? 'Create New' : 'Link Existing'}
                </button>
              ))}
            </div>
            {form.challengeMode === 'link' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Challenge</label>
                <select value={form.challengeId}
                  onChange={e => handleChange('challengeId', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500">
                  <option value="">Choose a challenge...</option>
                  {challenges.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
            {form.challengeMode === 'create' && (
              <p className="text-sm text-blue-700">
                A challenge will be auto-created using the event's location and dates when you save.
              </p>
            )}
          </CollapsibleSection>

          {/* NFT Reward */}
          <CollapsibleSection {...sectionProps} id="nft" title={t('cms.events.linkedNft')} bg="bg-purple-50/50">
            <p className="text-sm text-gray-600">
              NFT templates can be created after saving the event. Use the "Create NFT" action on the event card.
            </p>
            {event?.nft_template_id && event?.nft_template_name && (
              <div className="flex items-center gap-2 text-sm text-purple-700">
                <FiAward className="w-4 h-4" />
                Linked: {event.nft_template_name}
              </div>
            )}
          </CollapsibleSection>

          {/* Registration */}
          <CollapsibleSection {...sectionProps} id="registration" title="Registration" bg="bg-green-50/50">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.registrationEnabled}
                onChange={e => handleChange('registrationEnabled', e.target.checked)}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
              Enable registration
            </label>
            {form.registrationEnabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.events.capacity')}</label>
                    <input type="number" value={form.maxParticipants}
                      onChange={e => handleChange('maxParticipants', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                      placeholder="Unlimited" />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm mt-6">
                      <input type="checkbox" checked={form.isPublic}
                        onChange={e => handleChange('isPublic', e.target.checked)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                      Public event
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Opens</label>
                    <input type="datetime-local" value={form.registrationStart}
                      onChange={e => handleChange('registrationStart', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Closes</label>
                    <input type="datetime-local" value={form.registrationEnd}
                      onChange={e => handleChange('registrationEnd', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
                  </div>
                </div>
              </>
            )}
          </CollapsibleSection>

          {/* Header Image */}
          <CollapsibleSection {...sectionProps} id="header" title="Header Image">
            {headerPreview && (
              <img src={headerPreview} alt="Header preview" className="w-full h-40 object-cover rounded-lg" />
            )}
            <input type="file" accept="image/*" onChange={handleHeaderFileChange}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
          </CollapsibleSection>

          {/* SEO */}
          <CollapsibleSection {...sectionProps} id="seo" title="SEO">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
              <input type="text" value={form.metaTitle}
                onChange={e => handleChange('metaTitle', e.target.value)}
                maxLength={255}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
              <textarea value={form.metaDescription}
                onChange={e => handleChange('metaDescription', e.target.value)}
                rows={2} maxLength={500}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500" />
            </div>
          </CollapsibleSection>

          {/* Translations */}
          <CollapsibleSection {...sectionProps} id="translations" title={
            <span className="flex items-center gap-2">
              <FiGlobe className="w-4 h-4 text-blue-500" />
              Translations
              <span className="text-xs text-gray-400 font-normal">
                ({Object.keys(translations).filter(l => l !== 'en' && (translations[l]?.title || translations[l]?.description)).length} languages)
              </span>
            </span>
          }>
            <LanguageTabsEditor
              languages={availableLanguages}
              defaultLang="en"
              translations={translations}
              fields={[
                { key: 'title', label: 'Event Title', type: 'input', placeholder: 'Event title in this language' },
                { key: 'description', label: 'Description', type: 'textarea', rows: 3, placeholder: 'Event description...' },
                { key: 'content', label: 'Full Content', type: 'textarea', rows: 4, placeholder: 'Full event content/details...' }
              ]}
              onChange={(locale, field, value) => {
                if (locale === 'en') {
                  // For English, use handleChange which updates both form and translations
                  handleChange(field, value);
                } else {
                  // For other languages, just update translations
                  setTranslations(prev => ({
                    ...prev,
                    [locale]: { ...prev[locale], [field]: value }
                  }));
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Edit English (default) in the sections above. Add translations for other languages here.
            </p>
          </CollapsibleSection>

          {/* Tags */}
          <CollapsibleSection {...sectionProps} id="tags" title="Tags">
            <div className="flex flex-wrap gap-2">
              {form.tags.map(tag => (
                <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full flex items-center gap-1">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                placeholder="Add tag..." />
              <button type="button" onClick={addTag}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                Add
              </button>
            </div>
          </CollapsibleSection>

        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {t('cms.common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : t('cms.common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Import Calendar Modal ───────────────────────────────────

const ImportCalendarModal = ({ onClose, onImported, t }) => {
  const [activeTab, setActiveTab] = useState('upload'); // upload | feed
  const [icalFile, setIcalFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState('');

  // Feed sources
  const [sources, setSources] = useState([]);
  const [newSource, setNewSource] = useState({ name: '', url: '', sync_interval_minutes: 60, auto_publish: false, default_category: '' });
  const [loadingSources, setLoadingSources] = useState(false);

  useEffect(() => {
    if (activeTab === 'feed') loadSources();
  }, [activeTab]);

  const loadSources = async () => {
    setLoadingSources(true);
    try {
      const res = await eventsAPI.getCalendarSources();
      setSources(res.data || []);
    } catch (e) {
      toast.error('Failed to load calendar sources');
    } finally {
      setLoadingSources(false);
    }
  };

  const handleFileImport = async () => {
    if (!icalFile) return toast.error('Select a .ics file');
    setImporting(true);
    try {
      const res = await eventsAPI.importIcal(icalFile, {
        auto_publish: autoPublish ? 'true' : 'false',
        default_category: defaultCategory
      });
      toast.success(res.data.message);
      onImported();
    } catch (error) {
      toast.error('Failed to import calendar');
    } finally {
      setImporting(false);
    }
  };

  const handleAddSource = async () => {
    if (!newSource.name || !newSource.url) return toast.error('Name and URL are required');
    try {
      await eventsAPI.addCalendarSource(newSource);
      toast.success('Calendar source added');
      setNewSource({ name: '', url: '', sync_interval_minutes: 60, auto_publish: false, default_category: '' });
      loadSources();
    } catch (error) {
      toast.error('Failed to add calendar source');
    }
  };

  const handleSyncSource = async (id) => {
    try {
      const res = await eventsAPI.syncCalendarSource(id);
      toast.success(res.data.message);
      loadSources();
    } catch (error) {
      toast.error('Sync failed');
    }
  };

  const handleDeleteSource = async (id) => {
    if (!window.confirm('Remove this calendar source?')) return;
    try {
      await eventsAPI.deleteCalendarSource(id);
      toast.success('Source removed');
      loadSources();
    } catch (error) {
      toast.error('Failed to remove source');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Import Calendar</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'upload', label: 'Upload File', icon: FiUpload },
            { id: 'feed', label: 'Calendar Feed', icon: FiRss }
          ].map(tab => (
            <button key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-primary-600 border-b-2 border-primary-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {activeTab === 'upload' ? (
            <>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <input type="file" accept=".ics,.ical"
                  onChange={e => setIcalFile(e.target.files?.[0])}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
                {icalFile && (
                  <p className="mt-2 text-sm text-green-600">Selected: {icalFile.name}</p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={autoPublish}
                    onChange={e => setAutoPublish(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  Auto-publish imported events
                </label>
                <select value={defaultCategory}
                  onChange={e => setDefaultCategory(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                  <option value="">No category</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <button onClick={handleFileImport} disabled={importing || !icalFile}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors">
                {importing ? 'Importing...' : 'Import Events'}
              </button>
            </>
          ) : (
            <>
              {/* Add new source form */}
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-700 text-sm">Add Calendar Feed</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Feed name"
                    value={newSource.name}
                    onChange={e => setNewSource(s => ({ ...s, name: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" />
                  <input type="url" placeholder="https://example.com/calendar.ics"
                    value={newSource.url}
                    onChange={e => setNewSource(s => ({ ...s, url: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={newSource.auto_publish}
                      onChange={e => setNewSource(s => ({ ...s, auto_publish: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600" />
                    Auto-publish
                  </label>
                  <select value={newSource.default_category}
                    onChange={e => setNewSource(s => ({ ...s, default_category: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                    <option value="">No category</option>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <select value={newSource.sync_interval_minutes}
                    onChange={e => setNewSource(s => ({ ...s, sync_interval_minutes: parseInt(e.target.value) }))}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                    <option value={30}>Every 30 min</option>
                    <option value={60}>Every 1 hour</option>
                    <option value={360}>Every 6 hours</option>
                    <option value={1440}>Daily</option>
                  </select>
                </div>
                <button onClick={handleAddSource}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 transition-colors">
                  Add Source
                </button>
              </div>

              {/* Sources list */}
              <div className="space-y-2">
                <h3 className="font-medium text-gray-700 text-sm">Active Sources</h3>
                {loadingSources ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : sources.length === 0 ? (
                  <p className="text-sm text-gray-500">No calendar sources configured.</p>
                ) : (
                  sources.map(src => (
                    <div key={src.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{src.name}</p>
                        <p className="text-xs text-gray-500 truncate">{src.url}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs ${src.last_sync_status === 'success' ? 'text-green-600' : src.last_sync_status === 'error' ? 'text-red-600' : 'text-gray-400'}`}>
                            {src.last_synced_at ? `Last sync: ${formatDateTime(src.last_synced_at)}` : 'Never synced'}
                          </span>
                          <span className="text-xs text-gray-400">{src.events_imported} imported</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-3">
                        <button onClick={() => handleSyncSource(src.id)}
                          className="p-1.5 text-primary-500 hover:bg-primary-50 rounded transition-colors"
                          title="Sync now">
                          <FiRefreshCw className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteSource(src.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Remove">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {t('cms.common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Events;
